import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { useRestClient } from '@/contexts/QueryProvider'
import { useMemo } from 'react'
import { Control, FieldValues, Path } from 'react-hook-form'

export type WorkspaceProjectApplicationShape = {
  projectId: number
  applicationId: number
}

type WorkspaceProjectApplicationFieldsProps<T extends FieldValues & WorkspaceProjectApplicationShape> = {
  control: Control<T>
  orgId: number | undefined
  lockedProjectId?: number
  excludedAppIds?: number[]
  showProjectPicker?: boolean
}

export function useWorkspaceProjectApplicationOptions(
  orgId: number | undefined,
  lockedProjectId: number | undefined,
  excludedAppIds: number[] = []
) {
  const { getProjectsApiListOrganisationProjects, getApplicationsApiGetApplications } = useRestClient()

  const {
    data: projects,
    isPending: isProjectsPending,
    isError: isProjectsError,
  } = getProjectsApiListOrganisationProjects.useQuery(
    { query: { orgId: orgId! } },
    { enabled: !!orgId && lockedProjectId == null }
  )

  const {
    data: applications,
    isPending: isApplicationsPending,
    isError: isApplicationsError,
  } = getApplicationsApiGetApplications.useQuery()

  const excluded = useMemo(() => new Set(excludedAppIds), [excludedAppIds])

  const projectOptions = useMemo(
    () =>
      projects
        ?.filter((project) => !project.isFrozen)
        .map((project) => ({
          value: project.projectId || 0,
          label: project.name || '',
        })) ?? [],
    [projects]
  )

  const applicationOptions = useMemo(
    () =>
      applications
        ?.filter((app) => app.applicationId != null && !excluded.has(app.applicationId))
        .map((app) => ({
          value: app.applicationId || 0,
          label: app.appName || '',
          description: app.description,
        })) ?? [],
    [applications, excluded]
  )

  return {
    projectOptions,
    applicationOptions,
    loading: isApplicationsPending || (lockedProjectId == null && isProjectsPending),
    error: isApplicationsError || (lockedProjectId == null && isProjectsError),
  }
}

export function WorkspaceProjectApplicationFields<T extends FieldValues & WorkspaceProjectApplicationShape>({
  control,
  orgId,
  lockedProjectId,
  excludedAppIds = [],
  showProjectPicker = lockedProjectId == null,
}: WorkspaceProjectApplicationFieldsProps<T>) {
  const { projectOptions, applicationOptions } = useWorkspaceProjectApplicationOptions(
    orgId,
    lockedProjectId,
    excludedAppIds
  )

  return (
    <>
      {showProjectPicker && (
        <SingleSelectComboboxField
          control={control}
          name={'projectId' as Path<T>}
          label="Project"
          options={projectOptions}
          placeholder="Select a project..."
          searchPlaceholder="Search projects..."
          emptyMessage="No projects found."
          allowClear={false}
        />
      )}

      <SingleSelectComboboxField
        control={control}
        name={'applicationId' as Path<T>}
        label="Application"
        options={applicationOptions}
        placeholder="Select an application..."
        searchPlaceholder="Search applications..."
        emptyMessage="No applications found."
        allowClear={false}
      />
    </>
  )
}
